import { useId, useState } from 'react';
import { Link } from 'react-router';
import { LuCheck, LuChevronDown } from 'react-icons/lu';

import { cn } from '@mds/shared/lib/utils';
import { Button } from '@mds/shared/ui/shadcn/button';
import { RadioGroupItem } from '@mds/shared/ui/shadcn/radio-group';

import type { Perk } from './package-card';

/** A route to go to, or a handler to run — rendered as a link or a button to match. */
export type PackageRowAction = { label: string } & ({ to: string } | { onClick: () => void });

export type PackageRowProps = {
  /** "Daily Desk". */
  name: string;
  /** The figure alone — "$10", or "By quote". */
  price: string;
  /** What the figure buys — "/ day". Empty for a quote-only package. */
  unit: string;
  /** The one-liner under the name — "Hot desk, no reservation needed". */
  tag: string;
  /** The details list. Same shape as `PackageCard`'s, so one mapping feeds both. */
  perks?: (string | Perk)[];
  /** The main button at the foot of the details — "Book a day". */
  action?: PackageRowAction;
  /** A quieter second button beside it — "Ask on WhatsApp". */
  secondaryAction?: PackageRowAction;
  /** Ring + "Most popular", the same mark `PackageCard` draws. */
  featured?: boolean;
  /**
   * Makes the row one option of a `RadioGroup`, with this as its value. The
   * whole row picks it (a radio before the name, the row its label) and the
   * chevron alone opens the details. Needs to sit in a `RadioGroup` laid out
   * as the four-column `PACKAGE_ROW_PICK_LIST`.
   */
  value?: string;
  /** Whether this row is the group's value, for the ring and the tint. */
  picked?: boolean;
  /**
   * Less padding top and bottom, for a list that has to fit a fixed height
   * beside something else, like the booking page's column beside the map.
   */
  compact?: boolean;
  className?: string;
};

/**
 * The list a set of rows sits in. The rows lay their name, price and chevron on
 * these three columns through `subgrid`, so every price starts at the same x:
 * the price column is as wide as the widest price in the list, not in the row.
 * A row outside this grid has no columns to borrow and falls apart.
 */
export const PACKAGE_ROW_LIST = 'grid grid-cols-[minmax(0,1fr)_auto_auto] gap-3';

/** The same list with a column in front for the radio of pickable rows. */
export const PACKAGE_ROW_PICK_LIST = 'grid grid-cols-[auto_minmax(0,1fr)_auto_auto] gap-3';

function ActionButton({
  action,
  variant,
}: {
  action: PackageRowAction;
  variant: 'default' | 'outline';
}) {
  return 'to' in action ? (
    <Button asChild variant={variant} size="sm">
      <Link to={action.to}>{action.label}</Link>
    </Button>
  ) : (
    <Button variant={variant} size="sm" onClick={action.onClick}>
      {action.label}
    </Button>
  );
}

/**
 * A package as one compact line — name and tag on the left, price on the
 * right — for a phone, where a stack of flip cards is a screen each.
 *
 * Pressing the line opens the details in place, underneath it: the
 * description, the perks, and the action buttons. It stays in the page's flow,
 * so the list simply grows and the rows below move down. A pickable row
 * (`value`) is the exception: pressing the line picks it, and only its chevron
 * opens the details.
 *
 * The header (or the chevron) is a real button with `aria-expanded`/
 * `aria-controls`, and the closed panel is `inert`, so its links are out of
 * the tab order until it is open.
 */
export default function PackageRow({
  name,
  price,
  unit,
  tag,
  perks,
  action,
  secondaryAction,
  featured,
  value,
  picked,
  compact,
  className,
}: PackageRowProps) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const pickable = value !== undefined;

  // The name and the price, laid on the list's columns by `subgrid` so every
  // price starts at the same x. Shared by the row's two forms below.
  const summary = (
    <>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        {featured && (
          <span className="font-mono text-2xs font-bold text-brand-text uppercase">
            Most popular
          </span>
        )}
        {/* The `lg` sizes only reach a page that shows rows on desktop; the
            space page swaps to flip cards there. */}
        <span className="text-base font-medium">{name}</span>
        <span className="text-sm text-ink-muted">{tag}</span>
      </span>

      <span className="text-lg whitespace-nowrap text-brand-text">
        {price}
        {unit && <span className="ml-1 text-xs font-medium text-ink-muted lg:text-sm">{unit}</span>}
      </span>
    </>
  );

  const chevron = (
    <LuChevronDown
      aria-hidden="true"
      className={cn(
        'size-4 shrink-0 text-ink-muted transition-transform duration-(--duration-base) ease-standard',
        'motion-reduce:transition-none',
        open && 'rotate-180',
      )}
    />
  );

  // `pb-3.75`: 1px under the 16px top, so the list's rows come out level with
  // the seat map beside them on the booking page.
  const height = compact ? 'min-h-12 py-2.5' : 'min-h-16 pt-4 pb-3.75';

  return (
    <div
      className={cn(
        'col-span-full grid grid-cols-subgrid rounded-card bg-surface text-ink shadow-card',
        // The mid green, as on `PackageCard`'s white back — the bright mint is
        // 1.4:1 on white. Inset so it never spills into the gap between rows.
        (featured || picked) && 'ring-2 ring-brand-edge ring-inset',
        picked && 'bg-brand-wash',
        className,
      )}
    >
      {pickable ? (
        <>
          {/* The whole row picks the package: a label over the radio, the name
              and the price, so a press anywhere on them presses the radio. The
              radio is Radix's, so the group gives arrow keys between rows. */}
          <label
            className={cn(
              'col-span-3 grid cursor-pointer grid-cols-subgrid items-center rounded-card pl-4',
              height,
            )}
          >
            <RadioGroupItem value={value} aria-label={name} />
            {summary}
          </label>

          {/* The details on their own button, a 44px target, so opening them
              never picks the package. */}
          <button
            type="button"
            onClick={() => setOpen((isOpen) => !isOpen)}
            aria-expanded={open}
            aria-controls={panelId}
            aria-label={`${open ? 'Hide' : 'Show'} ${name} details`}
            className="mr-1 flex size-11 items-center justify-center self-center rounded-full hover:bg-raised focus-visible:outline-2 focus-visible:outline-focus"
          >
            {chevron}
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={() => setOpen((isOpen) => !isOpen)}
          aria-expanded={open}
          aria-controls={panelId}
          className={cn(
            // A subgrid of the list's columns, not a flex row — that is what
            // lines the prices up across rows. The gap comes from the list.
            'col-span-full grid grid-cols-subgrid items-center rounded-card px-4 text-left',
            height,
            'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus',
          )}
        >
          {summary}
          {chevron}
        </button>
      )}

      {/* `grid-rows` 0fr → 1fr animates to the content's own height, which a
          `height` transition cannot do without measuring it. */}
      <div
        id={panelId}
        inert={!open}
        className={cn(
          'col-span-full grid transition-[grid-template-rows] duration-(--duration-base) ease-standard',
          'motion-reduce:transition-none',
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          {/* Indented past the radio, so the details line up under the name. */}
          <div className={cn('flex flex-col gap-3 px-4 pt-2 pb-4', pickable && 'pl-11')}>
            {perks && perks.length > 0 && (
              <ul className="flex list-none flex-col gap-2 text-sm text-ink-secondary lg:text-base">
                {perks.map((perk) => {
                  const { label, icon: Icon = LuCheck } =
                    typeof perk === 'string' ? { label: perk, icon: undefined } : perk;

                  return (
                    <li key={label} className="flex items-start gap-2">
                      <Icon className="mt-1 size-3 shrink-0 text-brand-text" aria-hidden="true" />
                      {label}
                    </li>
                  );
                })}
              </ul>
            )}

            {(action || secondaryAction) && (
              <div className="flex flex-wrap gap-2 pt-1">
                {action && <ActionButton action={action} variant="default" />}
                {secondaryAction && <ActionButton action={secondaryAction} variant="outline" />}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
